import { PaginationProps } from 'antd/es/pagination';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { DEFAULT_TABLE_PAGE_SIZE } from '../tanstacktable';
import { getIsoDate, isSafeFiniteInteger } from './index';
import { defaultPaginationState } from './usePageSizeOptions';

export type FilterValueConverter = (
  prop: string,
  urlSearchParamValue: string | null,
) => any;

/**
 * Hook that keeps current filter and pagination (current page and selected page size)
 * in URL search parameters.
 *
 * @param filterKeys List of all allowed parameters in the filter
 * @param initialFilters Default filter parameters
 * @param initialPagination Default pagination parameters
 * @param filterValueConverter Function that converts URL search parameter to
 *   filter's property type (e.g. to number)
 * @param onInitialFilters
 */
export const useDirectoryNavigation = <T = Record<string, any>>(
  filterKeys: (keyof T)[],
  initialFilters?: Partial<T>,
  initialPagination: PaginationProps = defaultPaginationState,
  filterValueConverter: FilterValueConverter = defaultFilterValueConverter,
  onInitialFilters?: (filters: T) => unknown,
) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const setParams = useCallback(
    (params: URLSearchParams, opts?: { replace?: boolean }) => {
      setSearchParams(new URLSearchParams(params), opts);
    },
    [setSearchParams],
  );

  const [total, setTotal] = useState<number | undefined>();
  const currentPageRef = useRef<string | undefined>(undefined);

  const applyParams = useCallback(
    (
      filter?: Partial<T> | ((val: Partial<T>) => Partial<T>),
      pagination?: PaginationProps,
    ) => {
      const newParams = new URLSearchParams(searchParams);
      const filterValue = filter instanceof Function ? filter({} as T) : filter;
      const toBeRemoved = new Set(filterKeys);
      let filtersChanged = false;

      if (filterValue) {
        Object.getOwnPropertyNames(filterValue).forEach((key) => {
          toBeRemoved.delete(key as keyof T);
          const val = filterValue?.[key as keyof T];
          const { hasValue, value } = toSearchParam(key, val);
          const prev = searchParams.get(key);
          if (hasValue) {
            newParams.set(key, value ?? '');
            if (prev !== (value ?? '')) filtersChanged = true;
          } else {
            if (prev !== null) filtersChanged = true;
            newParams.delete(key);
          }
        });
      }

      // Remove any filter keys that were not in the new filter object
      toBeRemoved.forEach((key) => {
        if (newParams.has(key.toString())) {
          newParams.delete(key.toString());
          filtersChanged = true;
        }
      });

      // Reset pagination if filters changed
      if (filtersChanged && !pagination) {
        newParams.delete('pageNumber');
      }

      if (pagination?.current) {
        newParams.set('pageNumber', pagination.current.toString());
      }

      if (pagination?.pageSize) {
        newParams.set('pageSize', pagination.pageSize.toString());
      }

      setParams(newParams, { replace: true });
    },
    [filterKeys, searchParams, setParams],
  );

  const applyFilters = useCallback(
    (filter?: Partial<T> | ((val: Partial<T>) => Partial<T>)) => {
      applyParams(filter);
    },
    [applyParams],
  );

  const isInitialized = useRef(false);
  const [filters, pagination] = useMemo(() => {
    const updated = updateFromSearchParams(
      searchParams,
      total,
      filterKeys,
      isInitialized.current ? undefined : initialPagination,
      isInitialized.current ? undefined : initialFilters,
      filterValueConverter,
    );
    isInitialized.current = true;
    return updated;
  }, [
    filterKeys,
    initialFilters,
    initialPagination,
    searchParams,
    total,
    filterValueConverter,
  ]);

  const handlePageChange = useCallback(
    (page: number) => {
      if (currentPageRef.current) {
        currentPageRef.current = undefined;
        return;
      }

      const params = new URLSearchParams(searchParams);
      const val = page.toString();

      if (params.get('pageNumber') !== val) {
        params.set('pageNumber', val);
        // push new history entry for page navigation
        setParams(params, { replace: false });
      }
    },
    [searchParams, setParams],
  );

  const handlePageSizeChange = useCallback(
    (_page: number | undefined, pageSize: number) => {
      const params = new URLSearchParams(searchParams);
      const val = pageSize.toString();

      if (params.get('pageSize') !== val) {
        params.set('pageSize', val);
        // when page size changes we reset to first page
        params.delete('pageNumber');
        currentPageRef.current = '1';
        setParams(params, { replace: false });
      }
    },
    [searchParams, setParams],
  );

  const initialFiltersMergedWithInitialQueryRef = useRef(filters);
  const onInitialFiltersRef = useRef(onInitialFilters);
  // Run only once on mount
  useEffect(() => {
    // do not override if there are already search params
    if (searchParams.toString()) return;

    applyParams(
      initialFiltersMergedWithInitialQueryRef.current,
      initialPagination,
    );
    onInitialFiltersRef.current?.(
      initialFiltersMergedWithInitialQueryRef.current,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return useMemo(
    () => ({
      queryFilters: filters,
      handlePageChange,
      handlePageSizeChange,
      handleTotalChange: setTotal,
      pageIndex: pagination.current ? pagination.current - 1 : 0,
      pagination,
      setQueryFilters: applyFilters,
    }),
    [
      filters,
      handlePageChange,
      handlePageSizeChange,
      setTotal,
      pagination,
      applyFilters,
    ],
  );
};

export const defaultFilterValueConverter = (prop: string, val: string | null) =>
  searchParamToFilterValue(prop, val, ['validPeriod']);

export function hasFilterValue(value: unknown) {
  return !!value || value === false;
}

function hasParamValue(value: unknown) {
  if (typeof value === 'boolean') {
    return true;
  }

  return !!value;
}

export const createSearchParamToFilterValueConverter =
  ({
    arrayFields,
    numericFields,
    booleanFields,
  }: {
    arrayFields?: string[];
    numericFields?: string[];
    booleanFields?: string[];
  }) =>
  (prop: string, val: string | null) =>
    searchParamToFilterValue(
      prop,
      val,
      arrayFields,
      numericFields,
      booleanFields,
    );

export function searchParamToFilterValue(
  prop: string,
  val: string | null,
  arrayFields?: string[],
  numericFields?: string[],
  booleanFields?: string[],
) {
  if (arrayFields?.includes(prop)) {
    if (prop === 'validPeriod' && !val?.includes(',')) {
      val = (val || '') + ',';
    }
    return (val || '')
      .split(',')
      .filter((d) => {
        if (
          !numericFields?.includes(prop) &&
          (prop === 'validPeriod' || prop.endsWith('Date'))
        ) {
          // Date filter allows empty values
          return true;
        }

        return !!d;
      })
      .map((value) => toValue(prop, value, numericFields, booleanFields));
  }

  return toValue(prop, val, numericFields, booleanFields);
}

const toNumber = (val?: string | null, defaultValue?: number) => {
  return isSafeFiniteInteger(val) ? Number(val) : defaultValue;
};

const toValue = (
  prop: string,
  val: string | null,
  numericFields?: string[],
  booleanFields?: string[],
) => {
  if (prop === 'validPeriod') {
    return val || null;
  }

  if (numericFields?.includes(prop)) {
    return toNumber(val);
  }

  if (booleanFields?.includes(prop)) {
    return val === 'true' ? true : val === 'false' ? false : undefined;
  }

  return val;
};

const toSearchParam = (
  key: string,
  val: unknown,
): { hasValue: boolean; value: string | null } => {
  if (Array.isArray(val)) {
    const withValues = val.filter(hasParamValue).length;
    if (withValues === 0) {
      return { hasValue: false, value: null };
    }
    return {
      hasValue: true,
      value: val.map((item) => toSearchParam(key, item).value).join(','),
    };
  }

  let strVal = val?.toString() ?? null;
  if (typeof val === 'object') {
    if (val instanceof Date) {
      strVal = getIsoDate(val);
    }
  }

  return { hasValue: hasFilterValue(val), value: strVal };
};

/**
 * Initialize filters and pagination from URL search parameters.
 *
 * @param searchParams URL search parameters
 * @param total Total number of rows fetched
 * @param filterKeys List of all allowed parameters in the filter
 * @param fallbackPagination Default pagination parameters
 * @param fallbackFilters Default filter parameters
 * @param valueConverter Function that converts URL search parameter to
 *                       filter's property type (e.g. to number)
 */
export const updateFromSearchParams = <T = Record<string, any>>(
  searchParams: URLSearchParams,
  total: number | undefined,
  filterKeys: (keyof T)[],
  fallbackPagination?: PaginationProps,
  fallbackFilters?: Partial<T>,
  valueConverter?: (prop: string, val: string | null) => any,
): [T, PaginationProps] => {
  const filters = {} as T;
  const pagination: PaginationProps = {};

  for (const filterKey of filterKeys) {
    const key = filterKey.toString();
    if (searchParams.has(key)) {
      const param = searchParams.get(key);
      const val = valueConverter ? valueConverter(key, param) : param;
      if (hasFilterValue(val)) {
        filters[filterKey] = val;
      } else {
        filters[filterKey] = fallbackFilters?.[filterKey] || undefined!;
      }
    } else {
      filters[filterKey] = fallbackFilters?.[filterKey] || undefined!;
    }
  }

  if (searchParams.has('pageNumber')) {
    pagination.current = toNumber(
      searchParams.get('pageNumber'),
      fallbackPagination?.current ?? 1,
    );
  } else {
    pagination.current = fallbackPagination?.current ?? 1;
  }
  if (searchParams.has('pageSize')) {
    pagination.pageSize = toNumber(
      searchParams.get('pageSize'),
      fallbackPagination?.pageSize ?? DEFAULT_TABLE_PAGE_SIZE,
    );
  } else {
    pagination.pageSize =
      fallbackPagination?.pageSize ?? DEFAULT_TABLE_PAGE_SIZE;
  }
  pagination.total = total;

  return [filters, pagination];
};
